import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { AlertCircle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

interface ClearOpportunitiesConfirmationModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  workspaceName: string;
  deletionMode: 'dateRange' | 'all';
  dateStart?: Date | null;
  dateEnd?: Date | null;
  opportunitiesCount: number;
}

export const ClearOpportunitiesConfirmationModal = ({
  open,
  onCancel,
  onConfirm,
  isDeleting,
  workspaceName,
  deletionMode,
  dateStart,
  dateEnd,
  opportunitiesCount,
}: ClearOpportunitiesConfirmationModalProps) => {
  const { t } = useTranslation('admin');

  const formatDate = (date: Date | null | undefined) => date?.toLocaleDateString();

  const deletionSummary =
    deletionMode === 'all'
      ? t('admin.clearOpportunitiesConfirmationModal.allOpportunitiesInWorkspace', {
          workspaceName,
        })
      : t('admin.clearOpportunitiesConfirmationModal.opportunitiesInFromTo', {
          workspaceName,
          dateStart: formatDate(dateStart) ?? '',
          dateEnd: formatDate(dateEnd) ?? '',
        });

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isDeleting) onCancel();
      }}
    >
      <DialogContent className="max-w-160 gap-0 p-4" showCloseButton={!isDeleting}>
        <DialogHeader>
          <DialogTitle>
            {t('admin.clearOpportunitiesConfirmationModal.clearOpportunities')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4 py-4">
          <p className="text-base leading-6 text-foreground">
            <Trans
              i18nKey="admin.clearOpportunitiesConfirmationModal.youAreAboutToPermanentlyDelete"
              ns="admin"
              values={{ opportunitiesCount }}
              components={{ strong: <strong /> }}
            />
          </p>
          <p className="text-sm leading-5 text-muted-foreground">{deletionSummary}</p>
          <Alert
            className="border-transparent bg-warning text-warning-foreground"
            icon={<AlertCircle />}
          >
            {t('admin.clearOpportunitiesConfirmationModal.thisActionCannotBeUndone')}
          </Alert>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isDeleting}>
            {t('admin.clearOpportunitiesConfirmationModal.cancel')}
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting
              ? t('admin.clearOpportunitiesConfirmationModal.deleting')
              : t('admin.clearOpportunitiesConfirmationModal.deleteOpportunities', {
                  opportunitiesCount,
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
